import React from 'react';
import styled from 'styled-components';
import { Star } from 'lucide-react';
import { getBadgeImagePath } from '../utils/badgeAssets';

/**
 * Shared "How badges work" explainer, rendered in both:
 * - the UserProfileView "What are badges?" modal (dark → pass $onDark)
 * - the account-creation tutorial (light default)
 *
 * Single source of truth for the progression example, the one-badge-per-
 * category explainer, and the tier legend — edit here, both update.
 */

export const TIER_COLOR = {
  bronze: '#c56a2e',
  silver: '#a9a9a9',
  gold: '#faaf40'
};

const GAS_STATION_CATEGORY = 'gas_station';

// Step-by-step example of a single badge leveling up. Names are generic
// tiers (individual badge names are retired).
const GAS_STATION_PROGRESSION = [
  { number: 1, tier: 'bronze', name: 'Bronze', count: 1, caption: 'Start', desc: 'You review your first gas station.' },
  { number: 2, tier: 'silver', name: 'Silver', count: 10, caption: 'Level up', desc: 'You hit 10 gas station reviews.' },
  { number: 3, tier: 'gold', name: 'Gold', count: 25, caption: 'Mastered', desc: 'You hit 25 gas station reviews.' }
];

const ink = (alpha) => `rgba(35, 31, 32, ${alpha})`;
const paper = (alpha) => `rgba(255, 255, 255, ${alpha})`;

const HelpSectionTitle = styled.h3`
  margin: 0 0 12px;
  font-size: 1rem;
  text-align: ${(props) => (props.$stacked ? 'center' : 'left')};
  color: ${(props) => (props.$onDark ? '#ffffff' : '#231F20')};
`;

const HelpGrid = styled.div`
  display: flex;
  gap: 20px;
  align-items: stretch;
  flex-direction: ${(props) => (props.$stacked ? 'column' : 'row')};

  @media (max-width: 620px) {
    flex-direction: column;
  }
`;

const HelpExample = styled.div`
  flex: 1 1 55%;
  min-width: 0;
`;

const StepColumns = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
`;

const StepColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: ${(props) => (props.$stacked ? '4px' : '8px')};
  padding: ${(props) => (props.$stacked ? '10px 6px' : '14px 10px')};
  border-radius: 12px;
  background: ${(props) => (props.$onDark ? '#1a1a1a' : '#f5f5f5')};
  border: 1px solid ${(props) => (props.$onDark ? paper(0.08) : '#e0e0e0')};
`;

const StepTextBlock = styled.div`
  min-height: ${(props) => (props.$stacked ? '0' : '64px')};
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ProgressionStepImg = styled.img`
  width: ${(props) => (props.$stacked ? '48px' : '62px')};
  height: ${(props) => (props.$stacked ? '48px' : '62px')};
  object-fit: contain;
  margin-top: 4px;
`;

const StepCaption = styled.div`
  font-weight: 700;
  font-size: 0.9rem;
  color: ${(props) => (props.$onDark ? '#ffffff' : '#231F20')};
`;

const StepDesc = styled.div`
  font-size: 0.78rem;
  line-height: 1.4;
  color: ${(props) => (props.$onDark ? paper(0.65) : ink(0.65))};
`;

const StepName = styled.div`
  font-weight: 700;
  font-size: 0.9rem;
  color: ${(props) => (props.$onDark ? '#ffffff' : '#231F20')};
`;

const StepTier = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  color: ${(props) => props.$color};
`;

const InfoColumn = styled.div`
  flex: 1 1 45%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 10px;
  padding: ${(props) => (props.$stacked ? '12px 12px' : '20px 16px')};
  background: ${(props) => (props.$onDark ? '#1a1a1a' : '#f5f5f5')};
  border: 1px solid ${(props) => (props.$onDark ? paper(0.1) : '#e0e0e0')};
  border-radius: 12px;
`;

const InfoStarWrap = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #faaf40;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  margin-bottom: 4px;
`;

const InfoTitle = styled.h4`
  margin: 0;
  font-size: 1.05rem;
  color: ${(props) => (props.$onDark ? '#ffffff' : '#231F20')};
`;

const InfoText = styled.p`
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.5;
  color: ${(props) => (props.$onDark ? paper(0.65) : ink(0.65))};
`;

export const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  padding-top: 14px;
  border-top: 1px solid ${(props) => (props.$onDark ? paper(0.12) : '#e0e0e0')};
`;

export const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const LegendDot = styled.span`
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${(props) => props.$color};
  flex-shrink: 0;
`;

export const LegendText = styled.div`
  font-size: 0.78rem;
  line-height: 1.2;

  strong {
    display: block;
    font-size: 0.82rem;
    color: ${(props) => (props.$onDark ? '#ffffff' : '#231F20')};
  }

  span {
    color: ${(props) => (props.$onDark ? paper(0.6) : ink(0.6))};
  }
`;

function BadgeHelpContent({ onDark = false, $onDark = false, stacked = false, showLegend = true }) {
  // Accept both spellings: styled transient ($onDark) from JSX call sites
  // and plain (onDark). Either one selects the dark theme.
  const dark = onDark || $onDark;
  return (
    <>
      <HelpGrid $stacked={stacked}>
        <HelpExample>
          <HelpSectionTitle $onDark={dark} $stacked={stacked}>Example: Gas Station</HelpSectionTitle>
          <StepColumns>
            {GAS_STATION_PROGRESSION.map((step) => (
              <StepColumn key={step.tier} $onDark={dark} $stacked={stacked}>
                <StepTextBlock $stacked={stacked}>
                  <StepCaption $onDark={dark}>{step.number}. {step.caption}</StepCaption>
                  <StepDesc $onDark={dark}>{step.desc}</StepDesc>
                </StepTextBlock>
                <ProgressionStepImg
                  $stacked={stacked}
                  src={getBadgeImagePath(GAS_STATION_CATEGORY, step.tier)}
                  alt={`Gas Station ${step.tier} badge`}
                />
                <StepName $onDark={dark}>{step.name}</StepName>
                <StepTier $color={TIER_COLOR[step.tier]}>{step.count} / 25</StepTier>
              </StepColumn>
            ))}
          </StepColumns>
        </HelpExample>

        <InfoColumn $onDark={dark} $stacked={stacked}>
          <InfoStarWrap>
            <Star size={26} />
          </InfoStarWrap>
          <InfoTitle $onDark={dark}>One badge per Category</InfoTitle>
          <InfoText $onDark={dark}>
            Each category has one badge that levels up as you reach 1, 10 and 25 reviews.
          </InfoText>
          <InfoText $onDark={dark}>
            Thanks for helping grow The Crapper Mapper community. Every restroom you add
            helps someone doo better!
          </InfoText>
        </InfoColumn>
      </HelpGrid>

      {showLegend && (
      <Legend $onDark={dark}>
        <LegendItem>
          <LegendDot $color="#c56a2e" />
          <LegendText $onDark={dark}>
            <strong>Bronze (1)</strong>
            <span>1 review</span>
          </LegendText>
        </LegendItem>
        <LegendItem>
          <LegendDot $color="#a9a9a9" />
          <LegendText $onDark={dark}>
            <strong>Silver (10)</strong>
            <span>10 reviews</span>
          </LegendText>
        </LegendItem>
        <LegendItem>
          <LegendDot $color="#faaf40" />
          <LegendText $onDark={dark}>
            <strong>Gold (25)</strong>
            <span>25 reviews</span>
          </LegendText>
        </LegendItem>
        <LegendItem>
          <LegendDot $color="#e0e0e0" />
          <LegendText $onDark={dark}>
            <strong>Locked</strong>
            <span>Not earned yet</span>
          </LegendText>
        </LegendItem>
      </Legend>
      )}
    </>
  );
}

export default BadgeHelpContent;
